import useCartStore from '@/lib/state-stores/cartStore';
import { TCartItem } from '@/types/carts';
import { Link } from '@/i18n/routing';
import { Minus, Plus } from 'lucide-react';
import React, { useTransition } from 'react';
import SlideUpAnimation from '@/components/animation/SlideUpAnimation';
import { Routes } from '@/lib/routes/routes';
import Image from '@/components/Image';
import NextImage from 'next/image';
import { useTranslations } from 'next-intl';
import { generateSearchParams, IParams } from '@/lib/utils/createParams';
import Spinner from '@/components/ui/Spinner';
import DeleteImage from '@/public/trash.svg';
import SAR from '@/public/SAR';
import SaudiRealls from '@/components/sarComponent';
import { trackEvent } from '@/lib/amplitude';
import { AmplitudeEvents } from '@/lib/amplitude/events';
import CurrentLanguageText from '@/components/helper/CurrentLanguageText';

type TProps = {
  cart: TCartItem;
  branchId: string;
  onCloseCartSheet?: () => void;
  disableUpdate?: boolean;
  cardType: number;
};

const ProductCart = ({
  branchId,
  cart,
  onCloseCartSheet,
  disableUpdate = false,
  cardType,
}: TProps) => {
  const {
    defaultImage,
    itemArabicName,
    itemEnglishName,
    itemId,
    quantity,
    totalPrice,
    id,
    itemTypeId,
    isHaveValidDiscount,
    totalOriginalPrice,
    variantId,
    selectedVariantOptions,
    catItemStatus,
  } = cart;

  const ifProductHasVariant = Boolean(selectedVariantOptions?.length);

  const listOfProductVariants = selectedVariantOptions?.map((item) => ({
    key: item.varaintOptionEnglsihName.toLowerCase(),
    value: item.varaintOptionValueEnglishName,
  }));

  const params: IParams = {};
  listOfProductVariants?.forEach(({ key, value }) => {
    params[key] = value;
  });

  const isDigitalCard = cardType === 3;

  const productDetailsLink = isDigitalCard
    ? Routes.digitalCardDetails(itemId)
    : `${Routes.productDetails(branchId, itemId)}?${generateSearchParams(params)}`;

  const [isPending, startTransition] = useTransition();
  const { updateCart, removeFromCart, getCartIdByProductId } = useCartStore();
  const cartId = getCartIdByProductId(String(itemId));
  const t = useTranslations('cart');

  const handleAddCart = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    startTransition(async () => {
      await updateCart({
        branchId,
        itemId,
        itemTypeId,
        variantId,
        quantity: quantity + 1,
      });
      trackEvent(AmplitudeEvents.PRODUCT_QUANTITY_INCREASED, {
        branchId,
        productId: itemId,
        itemTypeId,
        variantId,
        productName: itemEnglishName,
        productPrice: totalPrice,
        productQuantity: quantity,
        storeId: Number(branchId),
        storeName: itemEnglishName,
      });
    });
  };

  const handleRemoveCart = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    startTransition(async () => {
      if (quantity > 1) {
        await updateCart({
          branchId,
          itemId,
          itemTypeId,
          variantId,
          quantity: quantity - 1,
        });
        trackEvent(AmplitudeEvents.PRODUCT_QUANTITY_DECREASED, {
          branchId,
          productId: itemId,
          itemTypeId,
          variantId,
          productName: itemEnglishName,
          productPrice: totalPrice,
          productQuantity: quantity,
          storeId: Number(branchId),
          storeName: itemEnglishName,
        });
      } else {
        await removeFromCart(String(cartId), String(id));
        trackEvent(AmplitudeEvents.PRODUCT_REMOVED_FROM_CART, {
          branchId,
          productId: itemId,
          itemTypeId,
          variantId,
          productName: itemEnglishName,
          productPrice: totalPrice,
          productQuantity: quantity,
          storeId: Number(branchId),
          storeName: itemEnglishName,
        });
      }
    });
  };

  const handleDelteCart = () => {
    startTransition(async () => {
      await removeFromCart(String(cartId), String(id));
    });
  };

  return (
    <div className="rounded-lg p-1 lg:py-3">
      <div className="flex justify-between">
        <div className="flex w-[70%] shrink-0 gap-3 lg:w-[75%]">
          <Link href={productDetailsLink} className="shrink-0" onClick={onCloseCartSheet}>
            <Image
              key={defaultImage}
              src={defaultImage ?? ''}
              width={120}
              local={!!isDigitalCard}
              height={120}
              alt="logo"
              className="h-14 w-14 rounded-lg object-cover shadow-sm lg:h-20 lg:w-20"
            />
          </Link>

          <div className="flex max-w-[75%] shrink-0 flex-col gap-2">
            <h3 className="me-2 line-clamp-1 text-sm font-bold lg:text-base">
              <CurrentLanguageText arabicName={itemArabicName} englishName={itemEnglishName} />
            </h3>

            {ifProductHasVariant && (
              <div className="lg:tex-sm text-gray-5 flex flex-wrap items-center gap-1 text-xs md:gap-2">
                {selectedVariantOptions?.map((item, index) => (
                  <div key={index} className="flex items-center">
                    <p>
                      <CurrentLanguageText
                        arabicName={item.varaintOptionArabicName}
                        englishName={item.varaintOptionEnglsihName}
                      />
                    </p>
                    <span className="bg-gray-5 mx-1 h-1 w-1 rounded-full" />
                    <p>
                      <CurrentLanguageText
                        arabicName={item.varaintOptionValueArabicName}
                        englishName={item.varaintOptionValueEnglishName}
                      />
                    </p>
                  </div>
                ))}
              </div>
            )}

            {/* update the cart  */}

            {!disableUpdate && (
              <>
                {' '}
                {catItemStatus.id !== 1 ? (
                  <button className="flex items-center gap-1" onClick={handleDelteCart}>
                    {isPending ? (
                      <Spinner size="small" />
                    ) : (
                      <NextImage
                        src={DeleteImage}
                        alt="trash"
                        width={15}
                        height={20}
                        className="h-4 w-4"
                      />
                    )}

                    <span className="text-xs font-bold text-red-500">{t('out-of-stock')}</span>
                  </button>
                ) : (
                  !isDigitalCard && (
                    <div
                      className={`${isPending ? 'pointer-events-none opacity-45' : ''} bg-primary-50/50 flex max-w-min items-center gap-4 rounded-full p-1`}
                    >
                      <button
                        onClick={handleAddCart}
                        className={`${quantity === 10 ? 'text-gray-5 pointer-events-none' : ''} flex h-8 w-8 items-center justify-center`}
                      >
                        <Plus size={22} strokeWidth={2} />
                      </button>

                      <SlideUpAnimation uniqueKey={quantity}>{quantity}</SlideUpAnimation>

                      <button
                        onClick={handleRemoveCart}
                        className="flex h-8 w-8 items-center justify-center"
                      >
                        {quantity > 1 ? (
                          <Minus size={22} strokeWidth={2} />
                        ) : (
                          <NextImage
                            src={DeleteImage}
                            alt="trash"
                            width={15}
                            height={20}
                            className="h-4 w-4"
                          />
                        )}
                      </button>
                    </div>
                  )
                )}
              </>
            )}
          </div>
        </div>
        <div className="lg text-gray flex flex-col font-semibold">
          <div className="flex flex-row items-center gap-1.5 text-sm" dir="ltr">
            <SAR width={16} height={16} className="h-auto w-auto" />
            <SlideUpAnimation uniqueKey={totalPrice?.toFixed(2)}>
              {totalPrice?.toFixed(2)}
            </SlideUpAnimation>
          </div>
          {isHaveValidDiscount && (
            <span className="mx-2 text-[10px] font-bold text-gray-200 line-through lg:text-sm">
              <SaudiRealls
                price={Number(totalOriginalPrice?.toFixed(2))}
                size="medium"
                isHaveDiscount={true}
              />
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductCart;
